import React, { useEffect } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { FormattedMessage, useIntl } from "react-intl"
import { Box, Button, Progress, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { useExistingPreAuth, ExistingPreAuth } from "../../state/useExistingPreAuth"
import { CostShareAmountBox } from "#/components/carrot-card-cost-share/steps/shared/CostShareAmountBox"
import { useMutation, UseMutationResult } from "@tanstack/react-query"
import ResponseError from "#/types/responseError"
import { carrotClient } from "#/utils/CarrotClient"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { getAmountsAndDates } from "#/components/carrot-card-cost-share/steps/shared/getAmountsAndDates"

function useCancelExistingPreAuth(): UseMutationResult<void, ResponseError, number> {
  return useMutation<void, ResponseError, number>(
    (id: number) => {
      return carrotClient.cancelPreAuth(id)
    },
    {
      retry: buildCarrotApiRetryPolicy(2)
    }
  )
}

export function CancelExistingAuthStep(): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()
  const { data, isLoading } = useExistingPreAuth()
  const { send } = useStateMachine()
  const { mutate: cancelExistingPreAuth } = useCancelExistingPreAuth()

  useEffect(() => {
    if (!isLoading && data?.preAuths?.length === 0) {
      send("")
    }
  }, [data?.preAuths.length, send, isLoading])

  if (isLoading || !data?.preAuths || data.preAuths.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", blockSize: "100%" }}>
        <Progress />
      </Box>
    )
  }

  // We can safely assume there will only be one active pre-auth
  const existingPreAuth: ExistingPreAuth = data?.preAuths[0]

  const { carrotCardAmount, personalPaymentAmount, createdDate, expirationDate } = getAmountsAndDates(
    existingPreAuth,
    intl
  )

  function handleCancelAuthorization(): void {
    cancelExistingPreAuth(existingPreAuth.id, {
      onSuccess: () => {
        send("")
      }
    })
  }

  return (
    <>
      <Box sx={(theme) => ({ display: "flex", justifyContent: "center", marginBlockEnd: theme.tokens.spacing.md })}>
        <Box
          component="img"
          src="/images/exclamation-mark-sign.png"
          srcSet="/images/exclamation-mark-sign-2x.png"
          alt=""
          sx={{ blockSize: "250px", inlineSize: "250px" }}
        />
      </Box>

      <Typography variant="h3" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xs })}>
        <FormattedMessage defaultMessage="Cancel existing authorization?" />
      </Typography>

      <Stack spacing={theme.tokens.spacing.md}>
        <Typography>
          <FormattedMessage defaultMessage="You can only have one Carrot Card authorization at a time. Please continue if you'd like to cancel the one below." />
        </Typography>

        <Typography>
          <FormattedMessage defaultMessage="If you don't cancel this authorization, you'll be able to create a new one after you use your Carrot Card for the following expense:" />
        </Typography>

        <CostShareAmountBox carrotCardAmount={carrotCardAmount} personalPaymentAmount={personalPaymentAmount} />

        <Typography>
          <FormattedMessage
            defaultMessage="This authorization was created on {createdDate}, and will expire on {expirationDate}."
            values={{
              createdDate,
              expirationDate
            }}
          />
        </Typography>
      </Stack>

      <Box
        sx={(theme) => ({
          display: "flex",
          justifyContent: "flex-end",
          marginBlockStart: theme.tokens.spacing.xxl,
          gap: theme.tokens.spacing.xs
        })}
      >
        <Button variant="outlined" color="secondary" onClick={() => send("CLOSE")}>
          <FormattedMessage defaultMessage="Close" />
        </Button>

        <Button color="error" onClick={handleCancelAuthorization}>
          <FormattedMessage defaultMessage="Cancel authorization and continue" />
        </Button>
      </Box>
    </>
  )
}
